/**
 * Amazon Analytics Dashboard.
 *
 * Displays Amazon marketplace analytics for a brand:
 * KPI cards, revenue/profitability trends, top SKUs, inventory health, returns.
 * Data comes from Azure SQL views via /api/amazon endpoints.
 */

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import {
  isDemoMode,
  getAmazonOverview,
  getAmazonMonthly,
  getAmazonTopSkus,
  getAmazonInventory,
  getAmazonReturns,
  clearApiCache,
} from "../utils/PowerBIApiCall";
import type {
  AmazonOverview,
  AmazonMonthlyProfitability,
  AmazonTopSku,
  AmazonInventoryHealth,
  AmazonReturnsSku,
} from "../utils/PowerBIApiCall";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  BarChart,
  LabelList,
} from "recharts";
import { getPortcoIdFromAmazonBrand } from "../utils/brandMapping";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import {
  formatCurrencyCompact as fmtCurrency,
  formatCurrency as fmtCurrencyFull,
  formatPct as fmtPct,
  formatNumber as fmtNum,
} from "../utils/format";
import { KpiCard, Delta } from "../ui-components/KpiCard";
import { SectionLabel } from "../ui-components/SectionLabel";
import { RefreshButton } from "../ui-components/RefreshButton";
import { SERIES, GRID, AXIS_TICK, TOOLTIP_STYLE } from "../utils/chartTheme";

// ============================================================================
// Formatters
// ============================================================================

/** "2026-03" -> "Mar 2026" */
function monthLabel(orderMonth: string): string {
  const [y, m] = orderMonth.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1);
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
}

// ============================================================================
// Monthly Revenue Trend (CY vs PY)
// ============================================================================

/**
 * Renders the YoY % above each current-year bar; hidden when YoY is null.
 * Typed loosely because recharts' LabelList `content` passes a wide prop bag.
 */
function YoyBarLabel(props: {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: string | number | null;
}) {
  const x = Number(props.x ?? 0);
  const y = Number(props.y ?? 0);
  const width = Number(props.width ?? 0);
  const value = props.value;
  if (value === null || value === undefined) return null;
  const num = Number(value);
  const up = num >= 0;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={11}
      fontWeight={600}
      fill={up ? SERIES.positive : SERIES.negative}
    >
      {`${up ? "+" : ""}${num.toFixed(0)}%`}
    </text>
  );
}

function MonthlyRevenueTrend({ data }: { data: AmazonMonthlyProfitability[] }) {
  const chartData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const byMonth = new Map<
      number,
      { month: number; label: string; cy: number; py: number }
    >();

    for (const row of data) {
      const [yearStr, monthStr] = row.order_month.split("-");
      const year = parseInt(yearStr);
      const monthNum = parseInt(monthStr);
      if (year !== currentYear && year !== currentYear - 1) continue;

      const existing = byMonth.get(monthNum) || {
        month: monthNum,
        label: new Date(2000, monthNum - 1).toLocaleString("en", {
          month: "short",
        }),
        cy: 0,
        py: 0,
      };
      if (year === currentYear) existing.cy = row.gross_revenue;
      else existing.py = row.gross_revenue;
      byMonth.set(monthNum, existing);
    }

    return Array.from(byMonth.values())
      .sort((a, b) => a.month - b.month)
      .map((r) => ({
        ...r,
        // YoY % only when both years have real revenue for that month
        yoy: r.py > 0 && r.cy > 0 ? ((r.cy - r.py) / r.py) * 100 : null,
      }));
  }, [data]);

  if (chartData.length === 0) return null;

  const hasYoy = chartData.some((d) => d.yoy !== null);

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Monthly Revenue — Current Year vs Prior Year
      </h3>
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="label" tick={AXIS_TICK} />
            <YAxis tickFormatter={fmtCurrency} tick={AXIS_TICK} width={70} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v, name) => [
                fmtCurrencyFull(Number(v ?? 0)),
                name === "cy" ? "Current Year" : "Prior Year",
              ]}
            />
            <Legend
              formatter={(v) => (v === "cy" ? "Current Year" : "Prior Year")}
            />
            <Bar dataKey="cy" fill={SERIES.primary} radius={[4, 4, 0, 0]}>
              {hasYoy && (
                <LabelList dataKey="yoy" content={YoyBarLabel as never} />
              )}
            </Bar>
            <Line
              dataKey="py"
              stroke={SERIES.priorYearLine}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ r: 3, fill: SERIES.priorYearLine }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Daily Revenue Chart (Last 30 Days)
// ============================================================================

function DailyRevenueChart({ data }: { data: AmazonOverview["daily"] }) {
  const chartData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      label: new Date(d.order_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    }));
  }, [data]);

  // Actual span shown — data is the most recent 30 available days, which may
  // trail "today" when the extract lags, so state the range explicitly.
  const rangeLabel =
    data.length > 0
      ? `${new Date(data[0].order_date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} – ${new Date(data[data.length - 1].order_date).toLocaleDateString(
          "en-US",
          { month: "short", day: "numeric", year: "numeric" },
        )}`
      : "";

  // Only reached when the brand has no daily rows at all (the query returns
  // the most recent 30 available days, so a lagging extract still renders).
  // Show an explicit placeholder rather than dropping the panel, which left
  // the charts row lopsided and looked like a rendering bug in demos.
  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4 flex flex-col">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Daily Revenue (Latest 30 Days)
        </h3>
        <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
          <svg
            className="w-8 h-8 text-gray-300 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3v18h18M7 14l3-3 3 3 5-6"
            />
          </svg>
          <p className="text-sm text-gray-500 font-medium">
            No daily sales data available
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Monthly figures above remain current.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Daily Revenue (Latest 30 Days)
        </h3>
        {rangeLabel && (
          <span className="text-xs text-gray-400">{rangeLabel}</span>
        )}
      </div>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="amzRevGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.3}
                />
                <stop offset="95%" stopColor={SERIES.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="label" tick={AXIS_TICK} interval={4} />
            <YAxis tickFormatter={fmtCurrency} tick={AXIS_TICK} width={65} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [fmtCurrencyFull(Number(v ?? 0)), "Revenue"]}
            />
            <Area
              type="monotone"
              dataKey="gross_revenue"
              stroke={SERIES.primary}
              strokeWidth={2}
              fill="url(#amzRevGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Fee Breakdown Chart
// ============================================================================

function FeeBreakdownChart({
  current,
  prior,
}: {
  current: AmazonMonthlyProfitability;
  prior: AmazonMonthlyProfitability | null;
}) {
  const bars = [
    {
      label: "Gross Revenue",
      value: current.gross_revenue,
      color: SERIES.primary,
    },
    {
      label: "FBA Fees",
      value: Math.abs(current.fba_fulfillment_fees),
      color: "#f87171",
    },
    {
      label: "Referral Fees",
      value: Math.abs(current.referral_fees),
      color: "#fb923c",
    },
    {
      label: "Margin After Fees",
      value: current.margin_after_fees,
      color: "#34d399",
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Fee Breakdown — {current.order_month}
      </h3>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} layout="vertical">
            <CartesianGrid {...GRID} />
            <XAxis type="number" tickFormatter={fmtCurrency} tick={AXIS_TICK} />
            <YAxis
              type="category"
              dataKey="label"
              tick={AXIS_TICK}
              width={120}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [fmtCurrencyFull(Number(v ?? 0))]}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {bars.map((b, i) => (
                <Cell key={i} fill={b.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {prior && (
        <div className="mt-2 text-xs text-gray-400 text-center">
          Prior month margin: {fmtPct(prior.margin_pct)}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Margin Trend (contribution $ + margin % over time)
// ============================================================================

function MarginTrend({ data }: { data: AmazonMonthlyProfitability[] }) {
  const chartData = useMemo(
    () =>
      data.slice(-12).map((r) => ({
        label: monthLabel(r.order_month),
        margin: r.margin_after_fees,
        marginPct: r.margin_pct,
      })),
    [data],
  );

  if (chartData.length < 2) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Margin After Fees — Contribution &amp; Rate
      </h3>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="label" tick={AXIS_TICK} />
            <YAxis
              yAxisId="left"
              tickFormatter={fmtCurrency}
              tick={AXIS_TICK}
              width={60}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => `${v}%`}
              tick={AXIS_TICK}
              width={40}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v, name) =>
                name === "margin"
                  ? [fmtCurrencyFull(Number(v ?? 0)), "Contribution"]
                  : [fmtPct(Number(v ?? 0)), "Margin %"]
              }
            />
            <Bar
              yAxisId="left"
              dataKey="margin"
              fill={SERIES.inventory}
              fillOpacity={0.45}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="right"
              dataKey="marginPct"
              stroke={SERIES.inventory}
              strokeWidth={2}
              dot={{ r: 3, fill: SERIES.inventory }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Top SKUs Table
// ============================================================================

function marginColor(pct: number): string {
  if (pct >= 30) return "text-emerald-600";
  if (pct >= 15) return "text-amber-600";
  return "text-red-600";
}

function stockBadge(days: number, qty: number): React.ReactElement {
  if (qty === 0)
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
        OOS
      </span>
    );
  if (days < 14)
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
        Low
      </span>
    );
  if (days > 180)
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
        Over
      </span>
    );
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">
      OK
    </span>
  );
}

const TOP_SKU_LIMIT = 10;

function TopSkusTable({ skus }: { skus: AmazonTopSku[] }) {
  if (skus.length === 0) return null;

  const shown = skus.slice(0, TOP_SKU_LIMIT);
  const topRevenue = shown.reduce((s, r) => s + r.gross_revenue, 0);
  const allRevenue = skus.reduce((s, r) => s + r.gross_revenue, 0);
  const shownSharePct = allRevenue > 0 ? (topRevenue / allRevenue) * 100 : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Top SKUs by Revenue
        </h3>
        <span className="text-xs text-gray-400">
          Top {shown.length} of {skus.length} · {fmtPct(shownSharePct)} of
          revenue
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-2 text-left">#</th>
              <th className="px-4 py-2 text-left">Product</th>
              <th className="px-4 py-2 text-right">Revenue</th>
              <th className="px-4 py-2 text-right">Units</th>
              <th className="px-4 py-2 text-right">Last 30d</th>
              <th className="px-4 py-2 text-right">Margin</th>
              <th className="px-4 py-2 text-center">Stock</th>
              <th className="px-4 py-2 text-right">Return %</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr
                key={s.sku}
                className="border-t border-gray-100 hover:bg-gray-50"
              >
                <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                  {s.revenue_rank}
                </td>
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900 truncate max-w-[240px]">
                    {s.product_name}
                  </div>
                  <div className="text-[11px] text-gray-400">{s.sku}</div>
                </td>
                <td className="px-4 py-2 text-right font-medium">
                  {fmtCurrencyFull(s.gross_revenue)}
                </td>
                <td className="px-4 py-2 text-right">{fmtNum(s.units_sold)}</td>
                <td className="px-4 py-2 text-right text-gray-600">
                  {fmtCurrencyFull(s.revenue_last_30d)}
                </td>
                <td
                  className={`px-4 py-2 text-right font-semibold ${marginColor(s.margin_pct)}`}
                >
                  {fmtPct(s.margin_pct)}
                </td>
                <td className="px-4 py-2 text-center">
                  {stockBadge(s.days_of_stock, s.fulfillable_qty)}
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {Math.round(s.days_of_stock)}d
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  {s.return_rate_pct > 10 ? (
                    <span className="text-red-600 font-semibold">
                      {fmtPct(s.return_rate_pct)}
                    </span>
                  ) : s.return_rate_pct > 5 ? (
                    <span className="text-amber-600">
                      {fmtPct(s.return_rate_pct)}
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      {fmtPct(s.return_rate_pct)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {skus.length > TOP_SKU_LIMIT && (
        <div className="px-4 py-2 border-t border-gray-100 text-center text-xs text-gray-400">
          +{skus.length - TOP_SKU_LIMIT} more SKUs not shown
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Inventory Health Summary
// ============================================================================

function statusBadgeColor(status: string): string {
  switch (status) {
    case "OUT_OF_STOCK":
      return "bg-red-100 text-red-700";
    case "LOW_STOCK":
      return "bg-amber-100 text-amber-700";
    case "OVERSTOCK":
      return "bg-blue-100 text-blue-700";
    case "NO_SALES":
      return "bg-gray-100 text-gray-500";
    default:
      return "bg-emerald-100 text-emerald-700";
  }
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function InventoryHealthPanel({ items }: { items: AmazonInventoryHealth[] }) {
  if (items.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const it of items) {
    counts[it.stock_status] = (counts[it.stock_status] || 0) + 1;
  }

  const alertItems = items.filter(
    (i) => i.stock_status === "OUT_OF_STOCK" || i.stock_status === "LOW_STOCK",
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">
          Inventory Health
        </h3>
      </div>
      <div className="p-4">
        {/* Status distribution */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(counts).map(([status, count]) => (
            <span
              key={status}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadgeColor(status)}`}
            >
              {statusLabel(status)}: {count}
            </span>
          ))}
        </div>

        {/* Alert items table */}
        {alertItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-1.5 text-left">Product</th>
                  <th className="px-3 py-1.5 text-center">Status</th>
                  <th className="px-3 py-1.5 text-right">FBA Qty</th>
                  <th className="px-3 py-1.5 text-right">Avg Daily</th>
                  <th className="px-3 py-1.5 text-right">Days Left</th>
                </tr>
              </thead>
              <tbody>
                {alertItems.map((i) => (
                  <tr key={i.sku} className="border-t border-gray-100">
                    <td className="px-3 py-1.5">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]">
                        {i.product_name}
                      </div>
                      <div className="text-[10px] text-gray-400">{i.sku}</div>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusBadgeColor(i.stock_status)}`}
                      >
                        {statusLabel(i.stock_status)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {fmtNum(i.fulfillable_qty)}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {i.avg_daily_units_30d.toFixed(1)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold text-red-600">
                      {i.days_of_stock_30d > 0
                        ? Math.round(i.days_of_stock_30d)
                        : "0"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {alertItems.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">
            All SKUs are healthy
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Returns Analysis Panel
// ============================================================================

function ReturnsPanel({ returns }: { returns: AmazonReturnsSku[] }) {
  if (returns.length === 0) return null;

  const totalUnits = returns.reduce((s, r) => s + r.units_sold, 0);
  const totalReturned = returns.reduce((s, r) => s + r.units_returned, 0);
  const totalLost = returns.reduce((s, r) => s + r.estimated_revenue_lost, 0);
  const overallReturnRate =
    totalUnits > 0 ? (totalReturned / totalUnits) * 100 : 0;

  const flagged = returns.filter(
    (r) =>
      r.return_risk_flag === "HIGH_RETURN_RATE" ||
      r.return_risk_flag === "ELEVATED_RETURN_RATE",
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">
          Returns Analysis
        </h3>
      </div>
      <div className="p-4">
        {/* Summary KPIs */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500">Overall Return Rate</div>
            <div
              className={`text-lg font-bold tabular-nums ${overallReturnRate > 5 ? "text-red-600" : "text-gray-900"}`}
            >
              {fmtPct(overallReturnRate)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500">Units Returned</div>
            <div className="text-lg font-bold tabular-nums text-gray-900">
              {fmtNum(totalReturned)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500">Est. Revenue Lost</div>
            <div className="text-lg font-bold tabular-nums text-red-600">
              {fmtCurrencyFull(totalLost)}
            </div>
          </div>
        </div>

        {/* Flagged SKUs */}
        {flagged.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-1.5 text-left">Product</th>
                  <th className="px-3 py-1.5 text-center">Risk</th>
                  <th className="px-3 py-1.5 text-right">Return %</th>
                  <th className="px-3 py-1.5 text-right">Lost Revenue</th>
                  <th className="px-3 py-1.5 text-left">Top Reason</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((r) => (
                  <tr key={r.sku} className="border-t border-gray-100">
                    <td className="px-3 py-1.5">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]">
                        {r.product_name}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          r.return_risk_flag === "HIGH_RETURN_RATE"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {r.return_risk_flag === "HIGH_RETURN_RATE"
                          ? "High"
                          : "Elevated"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold text-red-600">
                      {fmtPct(r.return_rate_pct)}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {fmtCurrencyFull(r.estimated_revenue_lost)}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs">
                      {r.top_return_reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {flagged.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-2">
            No high-risk return items
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Dashboard UI
// ============================================================================

function AmazonDashboardUI({
  brandCode,
  portcoId,
}: {
  brandCode: string;
  portcoId?: string;
}) {
  const [overview, setOverview] = useState<AmazonOverview | null>(null);
  const [monthly, setMonthly] = useState<AmazonMonthlyProfitability[]>([]);
  const [topSkus, setTopSkus] = useState<AmazonTopSku[]>([]);
  const [inventory, setInventory] = useState<AmazonInventoryHealth[]>([]);
  const [returns, setReturns] = useState<AmazonReturnsSku[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadData() {
    setError(null);
    try {
      const [ov, mo, sk, inv, ret] = await Promise.all([
        getAmazonOverview(brandCode),
        getAmazonMonthly(brandCode),
        getAmazonTopSkus(brandCode),
        getAmazonInventory(brandCode),
        getAmazonReturns(brandCode),
      ]);
      setOverview(ov);
      setMonthly(mo);
      setTopSkus(sk);
      setInventory(inv);
      setReturns(ret);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load Amazon data",
      );
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadData().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [brandCode]);

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await clearApiCache();
      await loadData();
    } finally {
      setIsRefreshing(false);
    }
  }

  if (loading) return <Loading />;
  if (error)
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-red-200 bg-red-50">
        <p className="text-red-700 font-medium">Error loading Amazon data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  if (!overview)
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-gray-200/70 bg-gray-50">
        <p className="text-gray-600">
          No Amazon data available for this brand.
        </p>
      </div>
    );

  const { currentMonth, priorMonth, daily } = overview;
  const latestDay = daily.length > 0 ? daily[daily.length - 1] : null;

  // Prior-year same month, for YoY growth (the headline metric for peers).
  // Falls back to null when history is absent — <Delta> then renders nothing.
  const monthlyByKey = new Map(monthly.map((r) => [r.order_month, r]));
  const priorYear = (() => {
    if (!currentMonth) return null;
    const [y, m] = currentMonth.order_month.split("-");
    return monthlyByKey.get(`${parseInt(y) - 1}-${m}`) ?? null;
  })();

  // Growth KPIs
  const revenue = currentMonth?.gross_revenue ?? 0;
  const units = currentMonth?.units_sold ?? 0;
  const orders = currentMonth?.orders ?? 0;
  const aov = orders > 0 ? revenue / orders : 0;
  const asp = units > 0 ? revenue / units : 0;

  // Profitability KPIs
  const marginAfterFees = currentMonth?.margin_after_fees ?? 0;
  const marginPct = currentMonth?.margin_pct ?? 0;
  const feePct =
    currentMonth && currentMonth.gross_revenue > 0
      ? (Math.abs(currentMonth.total_amazon_fees) /
          currentMonth.gross_revenue) *
        100
      : 0;
  const returnRate = currentMonth?.return_rate_pct ?? 0;

  const periodLabel = currentMonth ? monthLabel(currentMonth.order_month) : "";
  const throughLabel = latestDay
    ? new Date(latestDay.order_date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : periodLabel;

  const marginColorClass =
    marginPct >= 20
      ? "text-emerald-600"
      : marginPct >= 10
        ? "text-amber-600"
        : "text-red-600";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={portcoId ? `/portco/${portcoId}` : "/dashboard"}
            className="text-sm text-wmp-600 hover:text-wmp-700 hover:underline"
          >
            &larr; {portcoId ? "Back" : "Dashboard"}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1">
            {brandCode
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
            — Amazon Analytics
          </h1>
          {throughLabel && (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-gray-500">
                Data through {throughLabel}
              </p>
              {!latestDay && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                  Monthly only
                </span>
              )}
            </div>
          )}
        </div>
        <RefreshButton onClick={handleRefresh} refreshing={isRefreshing} />
      </div>

      {/* Sub-nav when accessed via portco route */}
      {portcoId && <PortcoSubNav portcoId={portcoId} />}

      {/* ============================ Sales & Growth ==================== */}
      <SectionLabel
        label="Sales & Growth"
        subtitle={periodLabel ? `Current period: ${periodLabel}` : undefined}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Revenue"
          value={fmtCurrencyFull(revenue)}
          deltas={
            <>
              <Delta
                current={revenue}
                prior={priorYear?.gross_revenue}
                label="YoY"
              />
              <Delta
                current={revenue}
                prior={priorMonth?.gross_revenue}
                label="MoM"
              />
            </>
          }
        />
        <KpiCard
          label="Units Sold"
          value={fmtNum(units)}
          deltas={
            <>
              <Delta
                current={units}
                prior={priorYear?.units_sold}
                label="YoY"
              />
              <Delta
                current={units}
                prior={priorMonth?.units_sold}
                label="MoM"
              />
            </>
          }
        />
        <KpiCard
          label="Orders"
          value={fmtNum(orders)}
          deltas={
            <>
              <Delta current={orders} prior={priorYear?.orders} label="YoY" />
              <Delta current={orders} prior={priorMonth?.orders} label="MoM" />
            </>
          }
        />
        <KpiCard
          label="Avg Order Value"
          value={fmtCurrencyFull(aov)}
          footnote={
            asp > 0 ? `${fmtCurrencyFull(asp)} avg selling price` : undefined
          }
        />
      </div>

      {/* Revenue trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MonthlyRevenueTrend data={monthly} />
        <DailyRevenueChart data={daily} />
      </div>

      {/* ============================ Profitability ==================== */}
      <SectionLabel
        label="Profitability"
        subtitle="Margin after Amazon fees (FBA + referral)"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Contribution"
          value={fmtCurrencyFull(marginAfterFees)}
          valueColor={marginAfterFees >= 0 ? "text-gray-900" : "text-red-600"}
          deltas={
            <Delta
              current={marginAfterFees}
              prior={priorYear?.margin_after_fees}
              label="YoY"
            />
          }
          footnote="after Amazon fees"
        />
        <KpiCard
          label="Margin %"
          value={fmtPct(marginPct)}
          valueColor={marginColorClass}
          footnote="of gross revenue"
        />
        <KpiCard
          label="Amazon Fee %"
          value={fmtPct(feePct)}
          footnote="of gross revenue"
        />
        <KpiCard
          label="Return Rate"
          value={fmtPct(returnRate)}
          valueColor={
            returnRate > 5
              ? "text-red-600"
              : returnRate > 2
                ? "text-amber-600"
                : "text-gray-900"
          }
          footnote="units returned ÷ sold"
        />
      </div>

      {/* Profitability charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MarginTrend data={monthly} />
        {currentMonth && (
          <FeeBreakdownChart current={currentMonth} prior={priorMonth} />
        )}
      </div>

      {/* ============================ Top Products ==================== */}
      <SectionLabel label="Top Products" subtitle="Ranked by gross revenue" />
      <TopSkusTable skus={topSkus} />

      {/* ==================== Inventory & Returns ==================== */}
      <SectionLabel label="Inventory & Returns" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InventoryHealthPanel items={inventory} />
        <ReturnsPanel returns={returns} />
      </div>
    </div>
  );
}

// ============================================================================
// Exported Component (handles auth wrapper)
// ============================================================================

export function AmazonDashboard() {
  const { brandCode } = useParams<{ brandCode: string }>();
  const { pathname } = useLocation();

  const isPortcoRoute = pathname.startsWith("/portco/amazon/");
  const portcoId =
    isPortcoRoute && brandCode
      ? getPortcoIdFromAmazonBrand(brandCode)
      : undefined;

  if (!brandCode) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-gray-200/70 bg-gray-50">
        <p className="text-gray-600">No brand specified</p>
      </div>
    );
  }

  if (isDemoMode()) {
    return <AmazonDashboardUI brandCode={brandCode} portcoId={portcoId} />;
  }

  // Always render inside MsalAuthenticationTemplate so MSAL owns the auth
  // timing: it waits for any in-flight interaction/initialization and attempts
  // silent SSO before ever redirecting. A manual `accounts.length` guard here
  // fired a sign-in redirect whenever accounts read empty mid-flight (right
  // after navigating to the tab), which is why Amazon re-prompted while the
  // other tabs — which use this same unconditional pattern — did not.
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      loadingComponent={Loading}
      errorComponent={ErrorComponent}
    >
      <AmazonDashboardUI brandCode={brandCode} portcoId={portcoId} />
    </MsalAuthenticationTemplate>
  );
}
